import React from "react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import type { UnitFeature } from "@/types";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import { useFacility } from "@/hooks/useFacility";
import { useIsAnnexFacility } from "@/hooks/useIsAnnexFacility";
import { scrollToMap, hasMapTab } from "@/utils/map/scrollToMap";

interface UnitFeaturesProps {
    features: UnitFeature[];
    tier?: string;
}

export const UnitFeatures = ({ features, tier }: UnitFeaturesProps) => {
    const bgClass =
        tier === "tierB"
            ? "tw:before:bg-[var(--secondary)]"
            : "tw:before:bg-[var(--primary)]";

    const [openId, setOpenId] = React.useState<number | null>(null);
    const [facilityTooltipOpen, setFacilityTooltipOpen] = React.useState(false);

    const { unitGroup } = useUnitItemContext();
    const { facility } = useFacility(unitGroup.representative_unit.facility_id);
    const { isAnnex } = useIsAnnexFacility(unitGroup.representative_unit.facility_id);

    const mapTabExists = hasMapTab();

    return (
        <ul className="unit-item-features">
            {facility && isAnnex && (
                <li
                    className={`unit-item-features__item`}
                    id={`feature-annex`}
                >
                    <TooltipProvider>
                        <Tooltip
                            open={facilityTooltipOpen}
                            onOpenChange={setFacilityTooltipOpen}
                        >
                            <TooltipTrigger asChild>
                                <span
                                    onClick={() => setFacilityTooltipOpen(!facilityTooltipOpen)}
                                    className="tw:font-bold"
                                >
                                    <span className="feature-icon"></span>
                                    {facility.name}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent className="tw:max-w-xs bg-white tw:shadow-md">
                                <div>
                                    <p className="tw:text-sm tw:text-black">
                                        This unit is located at our <strong>{facility.name} location</strong>. Annex locations have separate controlled-access entrances and are managed by our staff working from an office at the main location.
                                    </p>
                                    {mapTabExists && (
                                        <div className="tw:flex tw:justify-end tw:mt-2">
                                            <button
                                                aria-label="Scroll to Map"
                                                className="locations-trigger btn btn-primary"
                                                onClick={() => {
                                                    setFacilityTooltipOpen(false);
                                                    scrollToMap();
                                                }}
                                            >
                                                Show on map
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </li>
            )}
            {features.length > 0 ? features.map((feature) => (
                <li
                    key={feature.id}
                    className={`${bgClass} unit-item-features__item`}
                    id={`feature-${feature.id}`}
                >
                    {feature.description ? (
                        <TooltipProvider>
                            <Tooltip
                                open={openId === feature.id}
                                onOpenChange={(isOpen) =>
                                    setOpenId(isOpen ? feature.id : null)
                                }
                            >
                                <TooltipTrigger asChild>
                                    <span
                                        onClick={() =>
                                            setOpenId(
                                                openId === feature.id
                                                    ? null
                                                    : feature.id,
                                            )
                                        }
                                    >
                                        <span className="feature-icon"></span>
                                        {feature.name}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{feature.description}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        <span>{feature.name}</span>
                    )}
                </li>
            )) : <></>}
        </ul>
    );
};
